// THE PAGE IS THE WORKSPACE — the pure arithmetic behind Prepare & Send's
// zoom, its keyboard walk through the blanks, handing a blank to a seat
// by key, and where a popover lands on a page.
//
// CONSUMER: shared (the web prep screen + node-pool tests).
//
// Grant, 2026-09-29, on the screen as built: "the view of the document
// needs to be large. as a whole the attached view of the document is
// too small for the screen and not easy to work with." The screen
// rendered every page at exactly one screen pixel per PDF point beside
// a 440px panel, and had no zoom (PrepareAndSend.tsx's own header said
// the conversion helpers existed "for a future zoom control"). This is
// that control's arithmetic, kept out of the component so the numbers a
// signature box lands on are tested without a browser.
//
// The drawing this implements: docs/design/esign-prepare-and-send-
// redesign-mockup-v1.html, screens 2 and 3, and its keyboard table.

/** Zoom levels the buttons step through. "Fit" is computed, not listed. */
export const ZOOM_STEPS: readonly number[] = [0.5, 0.67, 0.8, 1, 1.25, 1.5, 2];

export const MIN_ZOOM = ZOOM_STEPS[0]!;
export const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1]!;

/** The scale that fits a page's width into the document area.
 *
 *  `availablePx` is the area's inner width; `gutterPx` is the breathing
 *  room kept either side so the page's shadow is not clipped. Never
 *  below MIN_ZOOM (a phone-width area still gets a page a person can
 *  read by panning) and never above MAX_ZOOM (a wide monitor does not
 *  blow a letter page up to a poster). Returns 1 when nothing is known
 *  yet, which is exactly the screen as it was. */
export function fitToWidthScale(availablePx: number, pageWidthPt: number, gutterPx = 32): number {
  if (!Number.isFinite(availablePx) || !Number.isFinite(pageWidthPt)) return 1;
  if (availablePx <= 0 || pageWidthPt <= 0) return 1;
  const raw = (availablePx - gutterPx) / pageWidthPt;
  if (!Number.isFinite(raw) || raw <= 0) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, round2(raw)));
}

/** The next listed zoom above (or below) the current one. From a "fit"
 *  scale that sits between two steps, plus goes to the step above and
 *  minus to the step below, so the buttons never do nothing. */
export function stepZoom(current: number, direction: 1 | -1): number {
  if (direction > 0) {
    const up = ZOOM_STEPS.find((z) => z > current + 1e-6);
    return up ?? MAX_ZOOM;
  }
  const down = [...ZOOM_STEPS].reverse().find((z) => z < current - 1e-6);
  return down ?? MIN_ZOOM;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Screen pixels inside a zoomed page → PDF points. The one place the
 *  division lives; every pointer handler divides through here. */
export function pxToPt(px: number, scale: number): number {
  return scale > 0 ? px / scale : px;
}

// ─── Reading order, for Tab ───────────────────────────────────────────

export interface Placed {
  key: string;
  pageNumber: number;
  x: number;
  y: number;
  height: number;
}

/** Down the page, then across it: the order a form reads. Two blanks on
 *  the same ruled line (within half a box height of each other) are
 *  ordered left to right rather than by a few points of vertical
 *  jitter, so Tab moves along the "$ ____ Earnest Money ____" line the
 *  way a person's eye does. */
export function readingOrder<T extends Placed>(fields: readonly T[]): T[] {
  return [...fields].sort((a, b) => {
    if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
    const tolerance = Math.max(4, Math.min(a.height, b.height) / 2);
    if (Math.abs(a.y - b.y) > tolerance) return a.y - b.y;
    return a.x - b.x;
  });
}

/** The key after (or before) `currentKey` in `orderedKeys`, wrapping
 *  at either end. With no current key, the first (or last). Null when
 *  there is nothing to walk. */
export function neighbourKey(
  orderedKeys: readonly string[],
  currentKey: string | null,
  direction: 1 | -1,
): string | null {
  if (orderedKeys.length === 0) return null;
  const i = currentKey ? orderedKeys.indexOf(currentKey) : -1;
  if (i < 0) return direction > 0 ? orderedKeys[0]! : orderedKeys[orderedKeys.length - 1]!;
  const next = (i + direction + orderedKeys.length) % orderedKeys.length;
  return orderedKeys[next]!;
}

// ─── Alt+number: hand to a seat ──────────────────────────────────────

/** Which signer Alt+N means. `signerKeysInOrder` is the send's signer
 *  list as the panel shows it (the order a saved map is taught in), so
 *  1 is the first person on the list and 4 the fourth. Null off the
 *  end, and for keys other than 1 to 4: a bare digit on a blank always
 *  TYPES (a price starts with a digit), so the modifier is what makes
 *  this a hand-off (Grant, 2026-09-29, the drawing's decision 4). */
export function seatForKey(key: string, signerKeysInOrder: readonly string[]): string | null {
  if (!/^[1-4]$/.test(key)) return null;
  const n = Number(key);
  return signerKeysInOrder[n - 1] ?? null;
}

// ─── Where a popover lands ────────────────────────────────────────────

export interface PopoverPlacement {
  /** Points, relative to the page's top-left. */
  left: number;
  top: number;
  /** Whether it opens below the blank (the default) or above it, when
   *  below would run off the page. */
  side: "below" | "above";
}

/** Anchor a popover to a blank without leaving the page. Below the
 *  blank, left-aligned to it; shifted left when it would overflow the
 *  right edge, and flipped above when it would overflow the bottom.
 *  All in points, because the popover sits inside the zoomed overlay
 *  and scales with the page. */
export function placePopover(
  field: { x: number; y: number; width: number; height: number },
  page: { widthPt: number; heightPt: number },
  popover: { widthPt: number; heightPt: number },
  gapPt = 6,
): PopoverPlacement {
  const margin = 8;
  let left = field.x;
  if (left + popover.widthPt > page.widthPt - margin) {
    left = Math.max(margin, page.widthPt - margin - popover.widthPt);
  }
  const belowTop = field.y + field.height + gapPt;
  if (belowTop + popover.heightPt <= page.heightPt - margin) {
    return { left, top: belowTop, side: "below" };
  }
  const aboveTop = Math.max(margin, field.y - gapPt - popover.heightPt);
  return { left, top: aboveTop, side: "above" };
}

// ─── The package is one job (PR 3, 2026-09-29) ───────────────────────
//
// Grant's decision 2 on the drawing: Accept all is package-wide, and the
// "you haven't opened this document" stop goes. Both rest on every
// document in the package having been read, so a walk, a count or a
// bulk accept can span them. The pieces below are the pure parts of
// that: the order Tab walks across documents, and what a document's
// dot on the rail says about it.

/** A walk key names a blank across the whole package: which document,
 *  then which field. Field keys are UUIDs, so the separator cannot
 *  collide with either half. */
const WALK_KEY_SEP = "\u0000";

export function packageWalkKey(documentId: string, fieldKey: string): string {
  return `${documentId}${WALK_KEY_SEP}${fieldKey}`;
}

export function splitWalkKey(walkKey: string): { documentId: string; fieldKey: string } {
  const at = walkKey.indexOf(WALK_KEY_SEP);
  if (at < 0) return { documentId: "", fieldKey: walkKey };
  return { documentId: walkKey.slice(0, at), fieldKey: walkKey.slice(at + 1) };
}

/** Every blank to walk, across the package: the documents in the order
 *  the client will read them, and within each the order the form
 *  reads. Tab at the last blank of one document lands on the first
 *  blank of the next; neighbourKey wraps the whole list. */
export function packageWalk<T extends Placed>(
  documents: readonly { documentId: string; fields: readonly T[] }[],
): string[] {
  const out: string[] = [];
  for (const d of documents) {
    for (const f of readingOrder(d.fields)) out.push(packageWalkKey(d.documentId, f.key));
  }
  return out;
}

/** What the dot beside a document's number on the folded rail says.
 *
 *    reading  the OS has not finished reading it yet (queued, or in
 *             progress) — nothing can be said about its blanks
 *    failed   the background read could not get or draw the file;
 *             opening the document retries with the full error
 *    todo     read, and something on it still needs a person
 *    done     read, and nothing on it is waiting
 *
 *  Order matters: a document that has been read is never "reading"
 *  again, and a read that succeeded is never "failed". */
export type DocumentDotState = "reading" | "failed" | "todo" | "done";

export function documentDotState(args: {
  /** Its fields and pages are known — read in the background, opened
   *  by the sender, or on screen now. */
  read: boolean;
  failed: boolean;
  todo: boolean;
}): DocumentDotState {
  if (args.read) return args.todo ? "todo" : "done";
  return args.failed ? "failed" : "reading";
}
